import {
  ReactElement,
  FC,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  CSSProperties,
} from 'react';
import { useSelector } from 'react-redux';

import {
  BrowserRouter,
  FABController,
  useScrollContainer,
} from '@beeline/lk-utils';

import classes from './App.module.scss';
import './styles/reset.scss';
import './styles/global.scss';
import { Main } from './main/Main';
import { FullscreenProvider } from './providers/FullscreenProvider/FullscreenProvider';
import { StoreProvider } from './store/StoreProvider';
import { getHeaderHeight } from './store/agent/selectors';
import { agentActions } from './store/agent/slice';
import { useAppDispatch } from './store/useAppDispatch';
import { fetchUserPermissions } from './store/user/services';

const App: FC = () => {
  const scrollContainer = useScrollContainer();
  const headerRef = useRef<HTMLElement | null | undefined>(null);

  const headerHeight = useSelector(getHeaderHeight);

  const handleErrors = useCallback((event: PromiseRejectionEvent) => {
    throw new Error(event.reason.message);
  }, []);

  useEffect(() => {
    window.addEventListener('unhandledrejection', handleErrors);

    return () => {
      window.removeEventListener('unhandledrejection', handleErrors);
    };
  }, [handleErrors]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  const handleResize = useCallback(() => {
    if (headerRef.current) {
      dispatch(
        agentActions.setHeaderHeight(
          headerRef.current.getBoundingClientRect().height,
        ),
      );
    }
    dispatch(
      agentActions.setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    addEventListener('resize', handleResize);

    return () => {
      removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    headerRef.current = scrollContainer?.querySelector('[class*="header"]');
    dispatch(
      agentActions.setHeaderHeight(
        headerRef.current?.getBoundingClientRect().height ?? 0,
      ),
    );
  }, [dispatch, scrollContainer]);

  const appStyles = useMemo(
    (): CSSProperties => ({
      height: `calc(100dvh - ${headerHeight}px)`,
    }),
    [headerHeight],
  );

  return headerHeight !== null ? (
    <FullscreenProvider>
      <div id='app' className={classes.app} style={appStyles}>
        <Main />
      </div>
    </FullscreenProvider>
  ) : null;
};

export function Service(): ReactElement {
  return (
    <>
      <FABController marginBottom={86} />
      <StoreProvider>
        <BrowserRouter basename='bookworkplace'>
          <App />
        </BrowserRouter>
      </StoreProvider>
    </>
  );
}
